import { useState, useEffect, useMemo, useCallback } from 'react';
import foodTrackingService, { FoodHistoryItem } from '../../services/foodTrackingService';

interface FoodHistoryModalProps {
  date: string;
  mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack';
  onClose: () => void;
  onSuccess: () => void;
}

const MEAL_LABELS: Record<string, string> = {
  breakfast: 'Mic Dejun',
  lunch: 'Prânz',
  dinner: 'Cină',
  snack: 'Gustare',
};

const MEAL_EMOJIS: Record<string, string> = {
  breakfast: '🌅',
  lunch: '☀️',
  dinner: '🌙',
  snack: '🍎',
};

const MEAL_BADGE_COLORS: Record<string, string> = {
  breakfast: 'bg-orange-100 text-orange-700',
  lunch: 'bg-yellow-100 text-yellow-700',
  dinner: 'bg-indigo-100 text-indigo-700',
  snack: 'bg-green-100 text-green-700',
};

export default function FoodHistoryModal({ date, mealType, onClose, onSuccess }: FoodHistoryModalProps) {
  const [history, setHistory] = useState<FoodHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState<FoodHistoryItem | null>(null);
  const [grams, setGrams] = useState(100);
  const [selectedMealType, setSelectedMealType] = useState<'breakfast' | 'lunch' | 'dinner' | 'snack'>(mealType);
  const [saving, setSaving] = useState(false);
  const [mealTime] = useState(() => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  });
  const [currentMealTime, setCurrentMealTime] = useState(mealTime);

  useEffect(() => {
    loadHistory();
  }, []);

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (selectedItem) setSelectedItem(null);
        else onClose();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [selectedItem, onClose]);

  const loadHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await foodTrackingService.getFoodHistory(100);
      setHistory(data);
    } catch (err) {
      console.error('Error loading food history:', err);
      setError('Nu s-a putut încărca istoricul. Încearcă din nou.');
    } finally {
      setLoading(false);
    }
  };

  // Memoized filtered list - recalculates only when history or searchQuery changes
  const filteredHistory = useMemo(() => {
    if (!searchQuery.trim()) return history;
    const q = searchQuery.toLowerCase();
    return history.filter(item =>
      item.foodName.toLowerCase().includes(q) ||
      (item.brand && item.brand.toLowerCase().includes(q))
    );
  }, [history, searchQuery]);

  const handleSelectItem = useCallback((item: FoodHistoryItem) => {
    setSelectedItem(item);
    setGrams(item.servingSize > 0 ? item.servingSize : 100);
    if (item.lastMealType && ['breakfast', 'lunch', 'dinner', 'snack'].includes(item.lastMealType)) {
      setSelectedMealType(item.lastMealType as 'breakfast' | 'lunch' | 'dinner' | 'snack');
    }
  }, []);

  // Memoized nutrition calculations - recalculate only when selectedItem or grams changes
  const nutrition = useMemo(() => {
    if (!selectedItem) return { calories: 0, protein: 0, carbs: 0, fats: 0 };
    const base = selectedItem.servingSize > 0 ? selectedItem.servingSize : 100;
    const factor = grams / base;
    return {
      calories: Math.round(selectedItem.calories * factor),
      protein: Math.round(selectedItem.protein * factor * 10) / 10,
      carbs: Math.round(selectedItem.carbs * factor * 10) / 10,
      fats: Math.round(selectedItem.fats * factor * 10) / 10,
    };
  }, [selectedItem, grams]);

  const handleAddToMeal = async () => {
    if (!selectedItem) return;
    setSaving(true);
    try {
      const [hours, minutes] = currentMealTime.split(':');
      const localDate = new Date(
        parseInt(date.split('-')[0]),
        parseInt(date.split('-')[1]) - 1,
        parseInt(date.split('-')[2]),
        parseInt(hours),
        parseInt(minutes),
        0
      );

      await foodTrackingService.createEntry({
        foodName: selectedItem.foodName,
        brand: selectedItem.brand || undefined,
        barcode: selectedItem.barcode || undefined,
        servingSize: grams,
        servingUnit: selectedItem.servingUnit || 'g',
        quantity: grams,
        nutrition: {
          calories: nutrition.calories,
          protein: nutrition.protein,
          carbs: nutrition.carbs,
          fat: nutrition.fats,
        },
        mealType: selectedMealType,
        date: localDate.toISOString(),
        imageUrl: selectedItem.imageUrl || undefined,
      });

      onSuccess();
      onClose();
    } catch (err: any) {
      alert(err.message || 'Eroare la adăugarea mesei');
    } finally {
      setSaving(false);
    }
  };

  const formatLastUsed = useCallback((dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Azi';
    if (diffDays === 1) return 'Ieri';
    if (diffDays < 7) return `Acum ${diffDays} zile`;
    if (diffDays < 30) return `Acum ${Math.floor(diffDays / 7)} săpt.`;
    return d.toLocaleDateString('ro-RO', { day: 'numeric', month: 'short' });
  }, []);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center sm:p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="bg-white rounded-t-2xl sm:rounded-2xl max-w-lg w-full flex flex-col shadow-2xl"
        style={{
          maxHeight: 'min(95vh, 700px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        {/* ── Header ── */}
        <div className="flex-shrink-0 bg-white border-b border-gray-100 px-5 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <div className="flex items-center gap-3 min-w-0">
            {selectedItem ? (
              <button
                onClick={() => setSelectedItem(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                aria-label="Înapoi"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            ) : (
              <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            )}
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-gray-900 truncate">
                {selectedItem ? selectedItem.foodName : 'Istoric Alimente'}
              </h2>
              <p className="text-xs text-gray-500">
                {selectedItem
                  ? `Adaugă la ${MEAL_LABELS[selectedMealType]}`
                  : loading ? 'Se încarcă...' : `${filteredHistory.length} produse${searchQuery ? ' găsite' : ' salvate'}`}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 flex-shrink-0 ml-2"
            aria-label="Închide"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* ── Scrollable Content ── */}
        <div
          className="flex-1 overflow-y-auto overscroll-contain"
          style={{ WebkitOverflowScrolling: 'touch' } as React.CSSProperties}
        >
          {!selectedItem ? (
            /* ── History List View ── */
            <>
              {/* Search bar - sticky inside scroll area */}
              <div className="sticky top-0 bg-white px-5 py-3 border-b border-gray-100 z-10">
                <div className="relative">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Caută în istoric..."
                    autoComplete="off"
                    className="w-full pl-9 pr-9 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      aria-label="Șterge căutarea"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              {/* States */}
              {loading ? (
                <div className="flex flex-col items-center justify-center py-16 gap-3">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600" />
                  <p className="text-sm text-gray-500">Se încarcă istoricul...</p>
                </div>
              ) : error ? (
                <div className="text-center py-12 px-6">
                  <div className="text-4xl mb-3">⚠️</div>
                  <p className="text-gray-600 mb-4">{error}</p>
                  <button
                    onClick={loadHistory}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors"
                  >
                    Încearcă din nou
                  </button>
                </div>
              ) : filteredHistory.length === 0 ? (
                <div className="text-center py-16 px-6">
                  <div className="text-5xl mb-4">🍽️</div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {searchQuery ? 'Niciun rezultat' : 'Niciun aliment în istoric'}
                  </h3>
                  <p className="text-gray-500 text-sm">
                    {searchQuery
                      ? `Nu am găsit "${searchQuery}" în istoricul tău`
                      : 'Adaugă alimente la mese și vor apărea aici pentru a le putea re-adăuga rapid'}
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-gray-50">
                  {filteredHistory.map((item, index) => (
                    <button
                      key={`${item.foodName}-${index}`}
                      onClick={() => handleSelectItem(item)}
                      className="w-full text-left px-5 py-4 hover:bg-purple-50 active:bg-purple-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        {/* Icon / Image */}
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {item.imageUrl ? (
                            <img
                              src={item.imageUrl}
                              alt=""
                              className="w-full h-full object-cover"
                              loading="lazy"
                              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                            />
                          ) : (
                            <span className="text-2xl" role="img" aria-hidden>
                              {MEAL_EMOJIS[item.lastMealType] || '🍎'}
                            </span>
                          )}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-gray-900 truncate max-w-[160px]">{item.foodName}</span>
                            {item.timesUsed > 1 && (
                              <span className="inline-flex items-center gap-1 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full flex-shrink-0 font-medium">
                                🔄 {item.timesUsed}×
                              </span>
                            )}
                          </div>
                          {item.brand && (
                            <p className="text-xs text-gray-500 truncate">{item.brand}</p>
                          )}
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <span className="text-xs text-gray-500">
                              {item.servingSize}{item.servingUnit} •{' '}
                              <span className="font-semibold text-orange-600">{item.calories} kcal</span>
                            </span>
                            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${MEAL_BADGE_COLORS[item.lastMealType] || 'bg-gray-100 text-gray-700'}`}>
                              {MEAL_LABELS[item.lastMealType] || item.lastMealType}
                            </span>
                            <span className="text-xs text-gray-400">{formatLastUsed(item.lastUsed)}</span>
                          </div>
                        </div>

                        {/* Arrow */}
                        <svg className="w-5 h-5 text-gray-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </button>
                  ))}

                  {/* Bottom padding for safe area */}
                  <div className="h-4" />
                </div>
              )}
            </>
          ) : (
            /* ── Detail / Add View ── */
            <div className="p-5 space-y-5">
              {/* Food Info Card */}
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-4 border border-purple-100">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-xl bg-white shadow-sm flex items-center justify-center overflow-hidden flex-shrink-0">
                    {selectedItem.imageUrl ? (
                      <img src={selectedItem.imageUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <span className="text-3xl" role="img" aria-hidden>
                        {MEAL_EMOJIS[selectedItem.lastMealType] || '🍎'}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-gray-900 truncate">{selectedItem.foodName}</h3>
                    {selectedItem.brand && (
                      <p className="text-sm text-gray-500 truncate">{selectedItem.brand}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-0.5">
                      Folosit de {selectedItem.timesUsed}× • {formatLastUsed(selectedItem.lastUsed)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Meal Type Selector */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Tip masă</label>
                <div className="grid grid-cols-4 gap-2">
                  {(['breakfast', 'lunch', 'dinner', 'snack'] as const).map((meal) => (
                    <button
                      key={meal}
                      type="button"
                      onClick={() => setSelectedMealType(meal)}
                      className={`p-2.5 rounded-xl border-2 transition-all flex flex-col items-center gap-1 ${
                        selectedMealType === meal
                          ? 'border-purple-500 bg-purple-50 text-purple-700'
                          : 'border-gray-200 hover:border-gray-300 text-gray-600'
                      }`}
                    >
                      <span className="text-xl" role="img" aria-hidden>{MEAL_EMOJIS[meal]}</span>
                      <span className="text-xs font-medium leading-tight text-center">{MEAL_LABELS[meal]}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Meal Time */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Ora mesei</label>
                <div className="relative">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <input
                    type="time"
                    value={currentMealTime}
                    onChange={(e) => setCurrentMealTime(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 font-medium text-gray-900 outline-none"
                  />
                </div>
              </div>

              {/* Grams Input */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Cantitate (grame)</label>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setGrams(g => Math.max(1, g - 10))}
                    className="w-11 h-11 rounded-full bg-red-100 text-red-600 font-bold text-xl flex items-center justify-center hover:bg-red-200 active:bg-red-300 transition-colors flex-shrink-0 select-none"
                    aria-label="Scade 10g"
                  >
                    −
                  </button>
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={grams === 0 ? '' : grams}
                      onChange={(e) => {
                        const raw = e.target.value.replace(/[^0-9]/g, '');
                        setGrams(raw === '' ? 0 : Math.min(9999, parseInt(raw, 10)));
                      }}
                      placeholder="0"
                      className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 text-center font-bold text-xl outline-none"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium pointer-events-none">g</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setGrams(g => Math.min(9999, g + 10))}
                    className="w-11 h-11 rounded-full bg-green-100 text-green-600 font-bold text-xl flex items-center justify-center hover:bg-green-200 active:bg-green-300 transition-colors flex-shrink-0 select-none"
                    aria-label="Adaugă 10g"
                  >
                    +
                  </button>
                </div>
                <p className="text-xs text-gray-400 text-center mt-1">± 10g per apăsare</p>
              </div>

              {/* Nutrition Preview - memoized values */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-2xl p-4">
                <p className="text-sm font-bold text-gray-700 mb-3 text-center">
                  Total pentru {grams}g
                </p>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { value: nutrition.calories, label: 'kcal', color: 'text-orange-600', size: 'text-xl' },
                    { value: `${nutrition.protein}g`, label: 'Prot', color: 'text-blue-600', size: 'text-lg' },
                    { value: `${nutrition.carbs}g`, label: 'Carbo', color: 'text-yellow-600', size: 'text-lg' },
                    { value: `${nutrition.fats}g`, label: 'Grăsimi', color: 'text-red-600', size: 'text-lg' },
                  ].map((n) => (
                    <div key={n.label} className="text-center bg-white rounded-xl p-2.5 shadow-sm">
                      <div className={`${n.size} font-bold ${n.color}`}>{n.value}</div>
                      <div className="text-xs text-gray-500">{n.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Bottom padding so footer doesn't cover content */}
              <div className="h-2" />
            </div>
          )}
        </div>

        {/* ── Footer Actions (only in detail view) ── */}
        {selectedItem && (
          <div className="flex-shrink-0 border-t border-gray-100 px-5 py-4 bg-white">
            <div className="flex gap-3">
              <button
                onClick={() => setSelectedItem(null)}
                disabled={saving}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-xl text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Înapoi
              </button>
              <button
                onClick={handleAddToMeal}
                disabled={saving || grams <= 0}
                className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-xl font-bold hover:bg-purple-700 active:bg-purple-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Adaug...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Adaugă la {MEAL_LABELS[selectedMealType]}
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
